import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import InvestorMetricGrid from "../investor/InvestorMetricGrid";
import InvestorSectionHeading from "../investor/InvestorSectionHeading";
import { notifyNotificationsUpdated } from "../../hooks/useNotificationUnreadCount";
import {
  fetchNotifications,
  markAllNotificationsRead,
  unwrapNotificationsPayload,
} from "../../services/notificationService";
import NotificationUnreadBadge from "./NotificationUnreadBadge";

const FILTER_ALL = "all";
const FILTER_UNREAD = "unread";

function formatListDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    }
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function messageSnippet(message, maxLen = 72) {
  const text = String(message ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}…`;
}

export default function NotificationInbox({ className = "" }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(FILTER_ALL);
  const [rows, setRows] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = filter === FILTER_UNREAD ? { unread_only: "true" } : {};
      const { data } = await fetchNotifications(params);
      const unpacked = unwrapNotificationsPayload(data);
      setRows(unpacked.results);
      setUnreadCount(unpacked.unread_count);
      notifyNotificationsUpdated();
    } catch {
      setError("Could not load notifications.");
      setRows([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const metrics = useMemo(
    () => [
      { key: "total", label: "Total alerts", value: String(rows.length), icon: "notifications" },
      {
        key: "unread",
        label: "Unread",
        value: String(unreadCount),
        icon: "notifications",
        highlight: unreadCount > 0,
        hint: unreadCount > 0 ? "Requires your attention" : "All caught up",
      },
    ],
    [rows.length, unreadCount]
  );

  const openNotice = (notice) => {
    navigate(`/notifications/${notice.id}`, { state: { notice } });
  };

  const handleMarkAll = async () => {
    if (unreadCount < 1) return;
    setMarkingAll(true);
    setSuccess("");
    setError("");
    try {
      await markAllNotificationsRead();
      setSuccess("All notifications marked as read.");
      await load();
    } catch {
      setError("Could not mark all as read.");
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <section className={["inv-notifications-inbox", className].filter(Boolean).join(" ")}>
      <InvestorMetricGrid metrics={metrics} />

      <div className="inv-notifications-toolbar">
        <div className="inv-notifications-toolbar__filters" role="tablist" aria-label="Notification filter">
          <button
            type="button"
            role="tab"
            aria-selected={filter === FILTER_ALL}
            className={filter === FILTER_ALL ? "inv-notifications-filter inv-notifications-filter--active" : "inv-notifications-filter"}
            onClick={() => setFilter(FILTER_ALL)}
          >
            All
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filter === FILTER_UNREAD}
            className={filter === FILTER_UNREAD ? "inv-notifications-filter inv-notifications-filter--active" : "inv-notifications-filter"}
            onClick={() => setFilter(FILTER_UNREAD)}
          >
            Unread
            <NotificationUnreadBadge count={unreadCount} className="inv-notifications-filter__badge" />
          </button>
        </div>
        <button
          type="button"
          className="btn-secondary inv-notifications-mark-all"
          disabled={unreadCount < 1 || markingAll}
          onClick={handleMarkAll}
        >
          {markingAll ? "Updating…" : "Mark all read"}
        </button>
      </div>

      {error ? <p className="msg msg-error">{error}</p> : null}
      {success ? <p className="msg msg-success">{success}</p> : null}

      {loading ? (
        <p className="muted inv-notifications-loading">Loading notifications…</p>
      ) : rows.length === 0 ? (
        <div className="inv-notifications-empty card">
          <InvestorSectionHeading icon="notifications" tag="h3">
            {filter === FILTER_UNREAD ? "No unread notifications" : "No notifications yet"}
          </InvestorSectionHeading>
          <p className="muted">
            {filter === FILTER_UNREAD
              ? "You have read everything in your inbox."
              : "Account alerts and platform notices will appear here when they are sent to you."}
          </p>
        </div>
      ) : (
        <ul className="inv-notifications-feed" aria-label="Notice inbox">
          {rows.map((n) => {
            const snippet = messageSnippet(n.message);
            return (
              <li
                key={n.id}
                className={[
                  "inv-notifications-feed__item",
                  n.is_read ? "inv-notifications-feed__item--read" : "inv-notifications-feed__item--unread",
                ].join(" ")}
              >
                <button
                  type="button"
                  className="inv-notifications-feed__row"
                  onClick={() => openNotice(n)}
                >
                  <span className="inv-notifications-feed__sender">Need Healthcare</span>
                  <span className="inv-notifications-feed__line">
                    <span className="inv-notifications-feed__subject">{n.title || "Notice"}</span>
                    {snippet ? (
                      <>
                        <span className="inv-notifications-feed__sep" aria-hidden>
                          {" "}
                          -{" "}
                        </span>
                        <span className="inv-notifications-feed__snippet">{snippet}</span>
                      </>
                    ) : null}
                  </span>
                  <time className="inv-notifications-feed__date" dateTime={n.created_at || undefined}>
                    {formatListDate(n.created_at)}
                  </time>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
